import React, { useEffect, useRef, useState } from "react";
import tw from "tailwind-styled-components";
import {
  MdFolder,
  MdInsertDriveFile,
  MdMoreVert,
  MdDelete,
} from "react-icons/md";
import { capitalizeFirstLetter } from "../../helpers";
import { FaSpinner } from "react-icons/fa";
import ConfirmModal from "../Common/ConfirmModal";

const DocumentCards = ({
  currentItems,
  handleFolderClick,
  formatDate,
  isDeleteLoading,
  handleDelete,
  deletingId
}) => {
  const [openMenuId, setOpenMenuId] = useState(null);
  const menuRef = useRef(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingPayload, setPendingPayload] = useState(null);

  const getUniqueKey = (item) => {
    return `${item.id}-${item.type}`;
  };

  const toggleMenu = (e, item) => {
    e.stopPropagation();
    e.preventDefault();
    const uniqueKey = getUniqueKey(item);
    setOpenMenuId(openMenuId === uniqueKey ? null : uniqueKey);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      const target = event.target;
      const isDeleteButton = target.closest('button[data-delete-button="true"]');

      if (!isDeleteButton && menuRef.current && !menuRef.current.contains(target)) {
        setOpenMenuId(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-4">
      {currentItems.map((item, index) => {
        const isFolder = item.type === "folder";
        const uniqueKey = getUniqueKey(item);

        return (
          <DocCard
            key={index}
            className={isFolder ? "cursor-pointer hover:bg-gray-50" : ""}
            onClick={() => isFolder && handleFolderClick(item)}
          >
            <div className="flex items-start justify-between w-full mb-2">
              <div className="flex items-center gap-2">
                {isFolder ? (
                  <MdFolder size={24} className="text-yellow-500" />
                ) : (
                  <MdInsertDriveFile size={24} className="text-blue-500" />
                )}
                <span className="text-sm font-semibold text-gray-800">
                  {capitalizeFirstLetter(isFolder ? item.name : item.title)}
                </span>
              </div>

              <div className="relative" ref={menuRef}>
                <button
                  onClick={(e) => toggleMenu(e, item)}
                  className="p-1 rounded-full hover:bg-gray-100"
                >
                  <MdMoreVert size={20} />
                </button>
                {openMenuId === uniqueKey && (
                  <div className="absolute right-0 mt-1 min-w-[160px] rounded-lg border bg-white shadow-lg z-50">
                    <button
                      data-delete-button="true"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();

                        if (isFolder && item.totalItems > 0) return;
                        setPendingPayload(item);
                        setShowConfirmModal(true);
                        //handleDelete(item);
                      }}
                      disabled={
                        (isFolder && item.totalItems > 0) ||
                        (isDeleteLoading && deletingId === item.id)
                      }
                      className={`flex w-full items-center gap-2 px-4 py-2 text-sm transition-colors ${isFolder && item.totalItems > 0
                        ? "cursor-not-allowed text-gray-400 bg-gray-50"
                        : "text-red-600 hover:bg-red-50"
                        }`}
                      title={
                        isFolder && item.totalItems > 0
                          ? "Folder contains items. Delete all items first."
                          : ""
                      }
                    >
                      {isDeleteLoading && deletingId === item.id ? (
                        <>
                          <FaSpinner className="animate-spin" size={16} />
                          Deleting...
                        </>
                      ) : (
                        <>
                          <MdDelete size={18} />
                          {isFolder ? "Delete folder" : "Delete file"}
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
            </div>

            {item.createdAt && (
              <span className="text-xs text-gray-400 mt-1">
                {formatDate(item.createdAt)}
              </span>
            )}

            {!isFolder ? (
              <a
                href={item.path}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-3 text-sm font-medium text-primary-color hover:underline"
                onClick={(e) => e.stopPropagation()}
              >
                View / Download
              </a>
            ) : (
              <span className="mt-3 text-xs text-gray-500">
                {item.totalItems || 0} item(s)
              </span>
            )}
          </DocCard>
        );
      })}
      <ConfirmModal
        isOpen={showConfirmModal}
        setOpenModel={setShowConfirmModal}
        title="Confirm Deletion"
        message={`Are you sure you want to delete this ${pendingPayload?.type}?`}
        onClose={() => {
          setShowConfirmModal(false);
          setPendingPayload(null);
        }}
        onConfirm={() => {
          handleDelete(pendingPayload);
        }}
        loading={isDeleteLoading}
        confirmBtnText="Confirm"
        confirmBtnClassName="bg-primary-color text-white border border-primary-color hover:opacity-90"
      />
    </div>
  );
};

const DocCard = tw.div`flex flex-col items-start bg-white border border-gray-200 p-4 rounded-xl shadow-sm hover:shadow-md transition-all duration-200`;

export default DocumentCards;